import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Bell, Clock, Gift, Shield, AlertTriangle,
  Loader2, Check, Info, BellRing, BellOff, Smartphone
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import API from '@/utils/api';
import AppLogo from '@/components/AppLogo';
import NotificationSettings from '@/components/settings/NotificationSettings';
import OfflineCacheSettings from '@/components/settings/OfflineCacheSettings';
import AppLockSettings from '@/components/settings/AppLockSettings';
import usePushNotifications from '@/hooks/usePushNotifications';
import { useDemo } from '@/contexts/DemoContext';

/**
 * NotificationSettingsPage - Calm notification system configuration
 * 
 * Design principle: "Only notify when the notification prevents a real problem."
 * 
 * This is a protective safety net, not a productivity notification system.
 * Notifications must feel like relief, not interruption.
 */
const NotificationSettingsPage = () => {
  const navigate = useNavigate();
  const { isDemoMode } = useDemo();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    notifications_enabled: true,
    leave_time_alerts: true,
    leave_time_buffer_minutes: 15,
    preparation_alerts: true,
    critical_alerts: true,
    max_notifications_per_day: 2
  });
  const [preview, setPreview] = useState(null);
  const [testSending, setTestSending] = useState(false);
  
  // Push notification hook
  const {
    isSupported: pushSupported,
    isSubscribed: pushSubscribed,
    permission: pushPermission,
    loading: pushLoading,
    subscribe: subscribeToPush,
    unsubscribe: unsubscribeFromPush,
    showLocalNotification,
  } = usePushNotifications();

  useEffect(() => {
    if (isDemoMode) {
      // In demo mode, use default settings and skip API calls
      setLoading(false);
      return;
    }
    fetchSettings();
    fetchPreview();
  }, [isDemoMode]);

  const fetchSettings = async () => {
    try {
      const response = await API.get('/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchPreview = async () => {
    try {
      const response = await API.get('/notifications/preview');
      setPreview(response.data);
    } catch (error) {
      console.error('Failed to fetch notification preview:', error);
    }
  };

  const updateSetting = async (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    
    // In demo mode, just update local state
    if (isDemoMode) {
      toast.success('Settings saved (demo)', { duration: 2000 });
      return;
    }
    
    setSaving(true);
    try {
      await API.put('/settings', { [key]: value });
      toast.success('Settings saved', { duration: 2000 });
      // Refresh preview when settings change
      fetchPreview();
    } catch (error) {
      console.error('Failed to update setting:', error);
      toast.error('Failed to save');
      // Revert on error
      setSettings(settings);
    } finally {
      setSaving(false);
    }
  };

  const getTotalUpcoming = () => {
    if (!preview) return 0;
    return (preview.today?.length || 0) + (preview.tomorrow?.length || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-blue-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-blue-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-200/60">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="p-2 text-slate-500 hover:text-slate-700"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <AppLogo size="sm" />
          </div>
          <h1 className="text-lg font-medium text-slate-800">Notifications</h1>
          <div className="w-10" /> {/* Spacer for centering */}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Philosophy Banner */}
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200/50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Shield className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-medium text-blue-900 mb-1">Calm Protection System</h3>
                <p className="text-sm text-blue-700/80">
                  Notifications are designed to prevent problems, not create pressure. 
                  You'll only hear from us when it truly helps.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Master Toggle */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  settings.notifications_enabled ? 'bg-emerald-100' : 'bg-slate-100'
                }`}>
                  <Bell className={`h-5 w-5 ${
                    settings.notifications_enabled ? 'text-emerald-600' : 'text-slate-400'
                  }`} />
                </div>
                <div>
                  <p className="font-medium text-slate-800">All Notifications</p>
                  <p className="text-sm text-slate-500">
                    {settings.notifications_enabled ? 'Protection enabled' : 'All alerts paused'}
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.notifications_enabled}
                onCheckedChange={(checked) => updateSetting('notifications_enabled', checked)}
                disabled={saving}
                data-testid="master-notifications-toggle"
              />
            </div>
          </CardContent>
        </Card>

        {/* Push Notifications Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-purple-500" />
              Push Notifications
            </CardTitle>
            <CardDescription>
              Get notified even when the app is closed
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Push Support Warning */}
            {!pushSupported && (
              <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-700 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>Push notifications are not supported in your browser.</span>
              </div>
            )}
            
            {/* Permission Blocked Warning */}
            {pushSupported && pushPermission === 'denied' && (
              <div className="p-3 bg-red-50 rounded-lg text-sm text-red-700 flex items-start gap-2">
                <BellOff className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>Notifications are blocked. Enable them in your browser settings.</span>
              </div>
            )}
            
            {/* Push Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  pushSubscribed ? 'bg-emerald-100' : 'bg-slate-200'
                }`}>
                  {pushSubscribed ? (
                    <BellRing className="h-5 w-5 text-emerald-600" />
                  ) : (
                    <BellOff className="h-5 w-5 text-slate-500" />
                  )}
                </div>
                <div>
                  <p className="font-medium text-slate-800">
                    {pushSubscribed ? 'Push Enabled' : 'Push Disabled'}
                  </p>
                  <span className="text-sm text-slate-500 block">
                    {pushPermission === 'granted' ? (
                      <Badge className="bg-emerald-100 text-emerald-700 text-xs">Allowed</Badge>
                    ) : pushPermission === 'denied' ? (
                      <Badge className="bg-red-100 text-red-700 text-xs">Blocked</Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700 text-xs">Not Set</Badge>
                    )}
                  </span>
                </div>
              </div>
              <Switch
                checked={pushSubscribed}
                onCheckedChange={async (checked) => {
                  if (checked) {
                    const result = await subscribeToPush();
                    if (result.success) {
                      toast.success('Push notifications enabled!');
                    } else {
                      toast.error(result.error || 'Failed to enable');
                    }
                  } else {
                    const result = await unsubscribeFromPush();
                    if (result.success) {
                      toast.success('Push notifications disabled');
                    }
                  }
                }}
                disabled={pushLoading || !pushSupported || pushPermission === 'denied'}
                data-testid="push-notifications-toggle"
              />
            </div>
            
            {/* Test Push Button */}
            {pushSubscribed && (
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                disabled={testSending}
                onClick={async () => {
                  setTestSending(true);
                  try {
                    await showLocalNotification('Test Notification', {
                      body: 'Push notifications are working!',
                    });
                    toast.success('Test notification sent!');
                  } catch (e) {
                    toast.error('Failed to send test');
                  } finally {
                    setTestSending(false);
                  }
                }}
              >
                {testSending ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" />Sending...</>
                ) : (
                  <><Bell className="h-4 w-4 mr-2" />Send Test Notification</>
                )}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Push Notifications Section */}
        <NotificationSettings />

        {/* App Lock Security Settings */}
        <AppLockSettings />

        {/* Offline Cache Settings */}
        <OfflineCacheSettings />

        {settings.notifications_enabled && (
          <>
            {/* Leave-Time Alerts */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      settings.leave_time_alerts ? 'bg-amber-100' : 'bg-slate-100'
                    }`}>
                      <Clock className={`h-5 w-5 ${
                        settings.leave_time_alerts ? 'text-amber-600' : 'text-slate-400'
                      }`} />
                    </div>
                    <div>
                      <CardTitle className="text-base">Leave-Time Alerts</CardTitle>
                      <CardDescription>Know when it's time to head out</CardDescription>
                    </div>
                  </div>
                  <Switch
                    checked={settings.leave_time_alerts}
                    onCheckedChange={(checked) => updateSetting('leave_time_alerts', checked)}
                    disabled={saving}
                    data-testid="leave-time-toggle"
                  />
                </div>
              </CardHeader>
              
              {settings.leave_time_alerts && (
                <CardContent className="pt-0">
                  <div className="pl-13 ml-10">
                    <p className="text-sm text-slate-600 mb-3">Alert me this many minutes before events:</p>
                    <RadioGroup
                      value={String(settings.leave_time_buffer_minutes)}
                      onValueChange={(value) => updateSetting('leave_time_buffer_minutes', parseInt(value))}
                      className="flex gap-4"
                    >
                      {[10, 15, 30].map((minutes) => (
                        <div key={minutes} className="flex items-center space-x-2">
                          <RadioGroupItem 
                            value={String(minutes)} 
                            id={`buffer-${minutes}`}
                            className="text-amber-600"
                          />
                          <Label 
                            htmlFor={`buffer-${minutes}`}
                            className="text-sm cursor-pointer"
                          >
                            {minutes} min
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                    <p className="text-xs text-slate-400 mt-2">
                      Example: "Leave in {settings.leave_time_buffer_minutes} minutes for Haircut"
                    </p>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Preparation Alerts */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      settings.preparation_alerts ? 'bg-pink-100' : 'bg-slate-100'
                    }`}>
                      <Gift className={`h-5 w-5 ${
                        settings.preparation_alerts ? 'text-pink-600' : 'text-slate-400'
                      }`} />
                    </div>
                    <div>
                      <CardTitle className="text-base">Preparation Alerts</CardTitle>
                      <CardDescription>Day-before reminders to prevent panic</CardDescription>
                    </div>
                  </div>
                  <Switch
                    checked={settings.preparation_alerts}
                    onCheckedChange={(checked) => updateSetting('preparation_alerts', checked)}
                    disabled={saving}
                    data-testid="preparation-toggle"
                  />
                </div>
              </CardHeader>
              
              {settings.preparation_alerts && (
                <CardContent className="pt-0">
                  <div className="pl-13 ml-10 space-y-2">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Check className="h-4 w-4 text-emerald-500" />
                      Birthday gift reminders (1-3 days before)
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Check className="h-4 w-4 text-emerald-500" />
                      Appointment reminders (day before)
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Check className="h-4 w-4 text-emerald-500" />
                      Reservation reminders (day of)
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Critical Protection Alerts */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      settings.critical_alerts ? 'bg-rose-100' : 'bg-slate-100'
                    }`}>
                      <AlertTriangle className={`h-5 w-5 ${
                        settings.critical_alerts ? 'text-rose-600' : 'text-slate-400'
                      }`} />
                    </div>
                    <div>
                      <CardTitle className="text-base">Critical Protection</CardTitle>
                      <CardDescription>Health, financial, essential reminders</CardDescription>
                    </div>
                  </div>
                  <Switch
                    checked={settings.critical_alerts}
                    onCheckedChange={(checked) => updateSetting('critical_alerts', checked)}
                    disabled={saving}
                    data-testid="critical-toggle"
                  />
                </div>
              </CardHeader>
              
              {settings.critical_alerts && (
                <CardContent className="pt-0">
                  <div className="pl-13 ml-10 space-y-2">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Check className="h-4 w-4 text-emerald-500" />
                      Prescription ending soon
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Check className="h-4 w-4 text-emerald-500" />
                      Membership renewals
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Check className="h-4 w-4 text-emerald-500" />
                      Car maintenance due
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Daily Limit */}
            <Card className="bg-slate-50/50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <Info className="h-5 w-5 text-slate-500" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-700 mb-1">Daily Limit</p>
                    <p className="text-sm text-slate-500">
                      Maximum <span className="font-semibold">{settings.max_notifications_per_day}</span> notifications per day, 
                      prioritized by importance. Health and family events come first.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Notification Preview */}
            {preview && getTotalUpcoming() > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Bell className="h-4 w-4" />
                    Upcoming Notifications
                  </CardTitle>
                  <CardDescription>
                    Based on your current events and settings
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {preview.today?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase mb-2">Today</p>
                      {preview.today.map((notif) => (
                        <div 
                          key={notif.id}
                          className="p-3 rounded-lg bg-amber-50 border border-amber-100 mb-2"
                        >
                          <p className="text-sm font-medium text-amber-900">{notif.title}</p>
                          <p className="text-sm text-amber-700">{notif.body}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {preview.tomorrow?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase mb-2">Tomorrow</p>
                      {preview.tomorrow.map((notif) => (
                        <div 
                          key={notif.id}
                          className="p-3 rounded-lg bg-blue-50 border border-blue-100 mb-2"
                        >
                          <p className="text-sm font-medium text-blue-900">{notif.title}</p>
                          <p className="text-sm text-blue-700">{notif.body}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* What We Never Do */}
        <Card className="bg-slate-50/50 border-dashed">
          <CardContent className="p-4">
            <p className="text-sm font-medium text-slate-600 mb-2">What we never do:</p>
            <div className="grid grid-cols-2 gap-2 text-xs text-slate-500">
              <div className="flex items-center gap-1">
                <span className="text-rose-400">✕</span> Daily summaries
              </div>
              <div className="flex items-center gap-1">
                <span className="text-rose-400">✕</span> Streak reminders
              </div>
              <div className="flex items-center gap-1">
                <span className="text-rose-400">✕</span> "Open the app" nudges
              </div>
              <div className="flex items-center gap-1">
                <span className="text-rose-400">✕</span> Pressure or guilt
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default NotificationSettingsPage;
